import type {Ref} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {Check, ChevronDown} from 'lucide-react';
import {Button, Tag, cn} from '@chekinapp/ui';
import {getInitials} from 'utils/common';
import {PropertyLinkShell} from 'pages/SearchReservation/components/PropertyLinkShell';
import type {GuestErrors} from 'hooks/auth/useGuestCreation';
import {StepHeading} from './StepParts';

const AVATAR_CLASS =
  'grid size-10 shrink-0 place-items-center rounded-xl bg-[color-mix(in_srgb,var(--chekin-color-brand-blue)_16%,var(--chekin-color-white))] text-[13px] font-bold text-[var(--chekin-color-brand-blue)]';

export type RegisteredGuest = {
  id: string;
  name: string;
  isComplete: boolean;
};

type Props = {
  columnRef: Ref<HTMLDivElement>;
  guests: RegisteredGuest[];
  failedNames: string[];
  guestErrors: GuestErrors;
  onComplete: () => void;
  onAddGuest: () => void;
  onLater: () => void;
};

function LeftStep({
  columnRef,
  guests,
  failedNames,
  guestErrors,
  onComplete,
  onAddGuest,
  onLater,
}: Props) {
  const {t, i18n} = useTranslation();
  const done = guests.filter(guest => guest.isComplete);
  const pending = guests.filter(guest => !guest.isComplete);
  const firstNames = done.map(guest => guest.name.split(' ')[0]);

  return (
    <PropertyLinkShell
      columnRef={columnRef}
      dock={
        <>
          {pending[0] ? (
            <Button className="w-full" onClick={onComplete}>
              {t('instant_check_in.complete_details')}
            </Button>
          ) : (
            failedNames.length > 0 && (
              <Button className="w-full" onClick={onAddGuest}>
                {t('instant_check_in.add_guest')}
              </Button>
            )
          )}
          <Button variant="ghost" className="w-full" onClick={onLater}>
            {t('instant_check_in.do_it_later')}
          </Button>
        </>
      }
    >
      {done.length > 0 && (
        <div className="mb-4 mt-1 flex items-center gap-2.5 rounded-[14px] bg-[var(--chekin-success-surface)] py-2 pl-2 pr-3 text-[13px] text-[var(--chekin-color-brand-navy)]">
          <span className="flex shrink-0" aria-hidden>
            {done.slice(0, 3).map((guest, index) => (
              <span
                key={guest.id}
                className={cn(
                  'grid size-[30px] place-items-center rounded-[9px] bg-[color-mix(in_srgb,var(--chekin-success)_24%,var(--chekin-color-white))] text-[11px] font-bold text-[var(--chekin-green-700)] [box-shadow:0_0_0_2px_var(--chekin-success-surface)]',
                  index > 0 && '-ml-2',
                )}
              >
                {getInitials(guest.name)}
              </span>
            ))}
          </span>
          <span className="min-w-0 flex-1">
            <Trans
              i18nKey="instant_check_in.names_checked_in"
              count={done.length}
              values={{
                names: new Intl.ListFormat(i18n.language, {type: 'conjunction'}).format(
                  firstNames,
                ),
              }}
              components={{b: <b className="font-semibold" />}}
            />
          </span>
          <Check
            size={16}
            strokeWidth={3}
            className="shrink-0 text-[var(--chekin-success)]"
          />
        </div>
      )}

      <StepHeading lead={t('instant_check_in.finish_before_arrival')}>
        {t('instant_check_in.whats_left')}
      </StepHeading>

      <ul className="flex flex-col gap-2.5">
        {pending.map(guest => (
          <li key={guest.id} className="rounded-[18px] bg-[var(--chekin-neutral-50)] p-3">
            <div className="flex items-center gap-3">
              <span className={AVATAR_CLASS}>{getInitials(guest.name)}</span>
              <span className="flex min-w-0 flex-1 flex-col items-start gap-1">
                <b className="truncate text-[15px] font-semibold text-[var(--chekin-color-brand-navy)]">
                  {guest.name}
                </b>
                <Tag tone="warning" fill="solid" size="xs">
                  {t('instant_check_in.details_pending')}
                </Tag>
              </span>
            </div>
            <p className="mt-2.5 text-[13px] leading-[18px] text-[color:var(--chekin-text-secondary)]">
              {t('instant_check_in.missing_details_hint')}
            </p>
          </li>
        ))}
        {failedNames.map(name => (
          <li
            key={`failed-${name}`}
            className="rounded-[18px] bg-[var(--chekin-danger-surface)] p-3"
          >
            <div className="flex items-center gap-3">
              <span className={AVATAR_CLASS}>{getInitials(name)}</span>
              <span className="flex min-w-0 flex-1 flex-col items-start gap-1">
                <b className="truncate text-[15px] font-semibold text-[var(--chekin-color-brand-navy)]">
                  {name}
                </b>
                <Tag tone="danger" size="xs">
                  {t('instant_check_in.couldnt_register')}
                </Tag>
              </span>
            </div>
            <p className="mt-2.5 text-[13px] leading-[18px] text-[var(--chekin-color-brand-navy)]">
              {t('instant_check_in.couldnt_register_hint')}
            </p>
            {guestErrors[name]?.length > 0 && (
              <details className="group mt-2">
                <summary className="inline-flex cursor-pointer list-none items-center gap-1 rounded-md text-[13px] font-semibold text-[var(--chekin-color-brand-navy)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--chekin-color-brand-blue)] [&::-webkit-details-marker]:hidden">
                  {t('instant_check_in.error_details')}
                  <ChevronDown
                    size={14}
                    aria-hidden
                    className="transition-transform group-open:rotate-180"
                  />
                </summary>
                <ul className="mt-1.5 flex flex-col gap-1 text-[12px] leading-[18px] text-[color:var(--chekin-text-secondary)]">
                  {guestErrors[name].map(message => (
                    <li key={message}>{message}</li>
                  ))}
                </ul>
              </details>
            )}
          </li>
        ))}
      </ul>
    </PropertyLinkShell>
  );
}

export {LeftStep};
